import {
  ABOUT_ICONS,
  ABOUT_MAX_LENGTH,
  ABOUT_WIDGET_HTML,
  aboutToHtml,
  escapeAboutText,
  hasAboutContent,
  safeAboutUrl,
  serializeAboutHtml,
} from "./aboutContent";

/**
 * The About section as blocks — the model the block editor edits and the
 * public page renders — over the HTML that is still the one thing stored
 * (`BookingPageSettings.AboutText`, see aboutContent.js).
 *
 * Every block is one top-level `<div class="ep-block ep-block-<type>">`. Parsing
 * is deliberately conservative: an element becomes an editable block only if
 * writing the block back out gives the very same markup. Anything else — a
 * hand-written tweak in HTML mode, content from before blocks existed that does
 * not fit one — is kept, byte for byte, as a "custom" block that only the HTML
 * mode edits. The editor can never lose what it cannot show.
 *
 * Older content (the three `ep-about-*` classes, a bare booking widget, plain
 * paragraphs) is read into the nearest block where that is safe, so a business
 * that used the previous editor can keep going with the new one.
 */

export const BLOCK_TYPES = ["text", "cards", "media", "faq", "link", "booking"];

export const ICON_NAMES = Object.keys(ABOUT_ICONS);

let nextId = 0;
const newId = () => `block-${(nextId += 1)}`;

// --- Small HTML helpers ------------------------------------------------------

const esc = escapeAboutText;

/** Plain text as paragraphs: a blank line separates them, a newline is a <br>. */
const textToParagraphs = (value, className = "") => {
  const cls = className ? ` class="${className}"` : "";
  return String(value || "")
    .trim()
    .split(/\n\s*\n/)
    .filter((part) => part.trim())
    .map((part) => `<p${cls}>${esc(part.trim()).replace(/\n/g, "<br>")}</p>`)
    .join("");
};

/** The reverse of textToParagraphs, or null when the markup is richer than text. */
const paragraphsToText = (elements) => {
  const parts = [];
  for (const element of elements) {
    if (element.tagName !== "P") return null;
    for (const child of element.childNodes) {
      if (child.nodeType === 1 && child.tagName !== "BR") return null;
    }
    const clone = element.cloneNode(true);
    clone.querySelectorAll("br").forEach((br) => br.replaceWith("\n"));
    parts.push(clone.textContent);
  }
  return parts.join("\n\n");
};

/** An element's text, or null when it holds markup a text field would lose. */
const plainText = (element) =>
  [...element.childNodes].every((child) => child.nodeType === 3) ? element.textContent : null;

const parseFragment = (html) =>
  new DOMParser().parseFromString(`<body>${html}</body>`, "text/html").body;

// Whitespace between block-level elements carries nothing; inside a paragraph
// it is a space between words. Comparisons ignore only the first kind.
const CONTAINERS = new Set(["BODY", "DIV", "FIGURE", "DETAILS", "UL", "OL"]);
const normalize = (html) => {
  const body = parseFragment(html);
  const walker = body.ownerDocument.createTreeWalker(body, 4 /* SHOW_TEXT */);
  const blanks = [];
  while (walker.nextNode()) {
    const node = walker.currentNode;
    if (!node.textContent.trim() && CONTAINERS.has(node.parentNode.tagName)) blanks.push(node);
  }
  blanks.forEach((node) => node.remove());
  return body.innerHTML;
};

const elementChildren = (element) => [...element.children];
const hasClass = (element, name) => element.classList.contains(name);

// --- Writing blocks out ------------------------------------------------------

const heading = (tag, text) => (text && text.trim() ? `<${tag}>${esc(text.trim())}</${tag}>` : "");

const blockClass = (type, extra = []) => ["ep-block", `ep-block-${type}`, ...extra].join(" ");

const writers = {
  text: (block) =>
    `<div class="${blockClass("text", block.align === "center" ? ["ep-align-center"] : [])}">${heading("h2", block.title)}${block.body || ""}</div>`,

  cards: (block) =>
    `<div class="${blockClass("cards")}">${heading("h2", block.title)}${textToParagraphs(block.intro, "ep-about-lead")}` +
    `<div class="ep-cards">${block.cards
      .map(
        (card) =>
          `<div class="ep-card${ABOUT_ICONS[card.icon] ? ` ep-icon-${card.icon}` : ""}">${heading("h3", card.title)}${textToParagraphs(card.text)}</div>`,
      )
      .join("")}</div></div>`,

  media: (block) => {
    const src = block.image?.src ? safeAboutUrl(block.image.src, true) : null;
    const figure = src ? `<figure><img src="${esc(src)}" alt="${esc(block.image.alt || "")}"></figure>` : "";
    const eyebrow = block.eyebrow && block.eyebrow.trim() ? `<p class="ep-eyebrow">${esc(block.eyebrow.trim())}</p>` : "";
    return (
      `<div class="${blockClass("media", [block.side === "right" ? "ep-media-right" : "ep-media-left"])}">${figure}` +
      `<div class="ep-media-body">${eyebrow}${heading("h2", block.title)}${block.body || ""}</div></div>`
    );
  },

  faq: (block) =>
    `<div class="${blockClass("faq")}">${heading("h2", block.title)}${block.items
      .map((item) => `<details><summary>${esc((item.question || "").trim())}</summary>${textToParagraphs(item.answer)}</details>`)
      .join("")}</div>`,

  link: (block) => {
    const href = block.href ? safeAboutUrl(block.href.trim()) : null;
    const cls = ["ep-button", ...(block.style === "outline" ? ["ep-button-outline"] : [])].join(" ");
    return (
      `<div class="${blockClass("link", block.align === "center" ? ["ep-align-center"] : [])}">` +
      `<a class="${cls}"${href ? ` href="${esc(href)}"` : ""}>${esc((block.text || "").trim())}</a></div>`
    );
  },

  booking: (block) =>
    `<div class="${blockClass("booking")}">${heading("h2", block.title)}${textToParagraphs(block.text)}${ABOUT_WIDGET_HTML}</div>`,

  custom: (block) => block.html,
};

export const blockToHtml = (block) => (writers[block.type] || writers.custom)(block);

/**
 * The blocks as the stored value: marker, sanitized HTML — or "" when nothing is
 * left, which hides the section. Throws `ABOUT_TOO_LONG` past the column's size.
 */
export const serializeBlocks = (blocks) => {
  const value = serializeAboutHtml(blocks.map(blockToHtml).join(""));
  if (value.length > ABOUT_MAX_LENGTH) throw new Error("ABOUT_TOO_LONG");
  return value;
};

// --- Reading blocks in -------------------------------------------------------

/** What a Text block's body may hold: what its rich-text field can edit. */
const BODY_TAGS = new Set(["P", "H2", "H3", "UL", "OL", "BLOCKQUOTE", "HR"]);

const readers = {
  text: (element) => {
    const children = elementChildren(element);
    let title = "";
    if (children[0]?.tagName === "H2") {
      title = plainText(children[0]);
      if (title === null) return null;
      children.shift();
    }
    if (!children.every((child) => BODY_TAGS.has(child.tagName))) return null;
    return {
      type: "text",
      title,
      body: children.map((child) => child.outerHTML).join(""),
      align: hasClass(element, "ep-align-center") ? "center" : "left",
    };
  },

  cards: (element) => {
    const children = elementChildren(element);
    let title = "";
    let intro = "";
    if (children[0]?.tagName === "H2") title = plainText(children.shift());
    const introElements = [];
    while (children[0]?.tagName === "P" && hasClass(children[0], "ep-about-lead")) introElements.push(children.shift());
    if (introElements.length) {
      const stripped = introElements.map((p) => {
        const clone = p.cloneNode(true);
        clone.removeAttribute("class");
        return clone;
      });
      intro = paragraphsToText(stripped);
    }
    const grid = children.shift();
    if (title === null || intro === null || !grid || children.length || !hasClass(grid, "ep-cards")) return null;
    const cards = elementChildren(grid).map((card) => {
      if (!hasClass(card, "ep-card")) return null;
      const parts = elementChildren(card);
      const cardTitle = parts[0]?.tagName === "H3" ? plainText(parts.shift()) : "";
      const text = paragraphsToText(parts);
      const icon = ICON_NAMES.find((name) => hasClass(card, `ep-icon-${name}`)) || "";
      return cardTitle === null || text === null ? null : { icon, title: cardTitle, text };
    });
    return cards.includes(null) ? null : { type: "cards", title, intro, cards };
  },

  media: (element) => {
    const children = elementChildren(element);
    let image = null;
    if (children[0]?.tagName === "FIGURE") {
      const img = children.shift().querySelector("img");
      image = img ? { src: img.getAttribute("src") || "", alt: img.getAttribute("alt") || "" } : null;
    }
    const body = children.shift();
    if (!body || children.length || !hasClass(body, "ep-media-body")) return null;
    const parts = elementChildren(body);
    let eyebrow = "";
    if (parts[0]?.tagName === "P" && hasClass(parts[0], "ep-eyebrow")) eyebrow = plainText(parts.shift());
    let title = "";
    if (parts[0]?.tagName === "H2") title = plainText(parts.shift());
    if (eyebrow === null || title === null || !parts.every((part) => BODY_TAGS.has(part.tagName))) return null;
    return {
      type: "media",
      eyebrow,
      title,
      body: parts.map((part) => part.outerHTML).join(""),
      image,
      side: hasClass(element, "ep-media-right") ? "right" : "left",
    };
  },

  faq: (element) => {
    const children = elementChildren(element);
    const title = children[0]?.tagName === "H2" ? plainText(children.shift()) : "";
    if (title === null) return null;
    const items = children.map((details) => {
      if (details.tagName !== "DETAILS") return null;
      const parts = elementChildren(details);
      const summary = parts.shift();
      const question = summary?.tagName === "SUMMARY" ? plainText(summary) : null;
      const answer = paragraphsToText(parts);
      return question === null || answer === null ? null : { question, answer };
    });
    return items.includes(null) ? null : { type: "faq", title, items };
  },

  link: (element) => {
    const children = elementChildren(element);
    const anchor = children[0];
    if (children.length !== 1 || anchor.tagName !== "A" || !hasClass(anchor, "ep-button")) return null;
    const text = plainText(anchor);
    if (text === null) return null;
    return {
      type: "link",
      text,
      href: anchor.getAttribute("href") || "",
      style: hasClass(anchor, "ep-button-outline") ? "outline" : "solid",
      align: hasClass(element, "ep-align-center") ? "center" : "left",
    };
  },

  booking: (element) => {
    const children = elementChildren(element);
    const title = children[0]?.tagName === "H2" ? plainText(children.shift()) : "";
    const widget = children.pop();
    if (title === null || widget?.getAttribute("data-booking-widget") !== "true") return null;
    const text = paragraphsToText(children);
    return text === null ? null : { type: "booking", title, text };
  },
};

/** A stored block element as an editable block, if — and only if — it round-trips. */
const readBlock = (element) => {
  const type = BLOCK_TYPES.find((name) => hasClass(element, `ep-block-${name}`));
  if (element.tagName !== "DIV" || !hasClass(element, "ep-block") || !type) return null;
  let block = null;
  try {
    block = readers[type](element);
  } catch {
    return null;
  }
  if (!block || normalize(blockToHtml(block)) !== normalize(element.outerHTML)) return null;
  return block;
};

// Content written before blocks: read into the nearest block where nothing is
// lost by it, or left as it is.
const readLegacyGrid = (element) => {
  const children = elementChildren(element);
  if (!children.length) return null;
  const figureFirst = children[0].tagName === "FIGURE";
  if (children.length === 2 && children.some((child) => child.tagName === "FIGURE")) {
    const figure = children.find((child) => child.tagName === "FIGURE");
    const card = children.find((child) => child !== figure);
    const img = figure.querySelector("img");
    if (!img || elementChildren(figure).length !== 1 || !hasClass(card, "ep-about-card")) return null;
    const parts = elementChildren(card);
    const title = parts[0]?.tagName === "H3" ? plainText(parts.shift()) : "";
    if (title === null || !parts.every((part) => BODY_TAGS.has(part.tagName))) return null;
    return {
      type: "media",
      eyebrow: "",
      title,
      body: parts.map((part) => part.outerHTML).join(""),
      image: { src: img.getAttribute("src") || "", alt: img.getAttribute("alt") || "" },
      side: figureFirst ? "left" : "right",
    };
  }
  const cards = children.map((card) => {
    if (!hasClass(card, "ep-about-card")) return null;
    const parts = elementChildren(card);
    const title = parts[0]?.tagName === "H3" ? plainText(parts.shift()) : "";
    const text = paragraphsToText(parts);
    return title === null || text === null ? null : { icon: "", title, text };
  });
  return cards.includes(null) ? null : { type: "cards", title: "", intro: "", cards };
};

const textRunToBlock = (elements) => {
  const children = [...elements];
  let title = "";
  if (children[0]?.tagName === "H2" && plainText(children[0]) !== null) title = plainText(children.shift());
  return { type: "text", title, body: children.map((child) => child.outerHTML).join(""), align: "left" };
};

/** The stored value (legacy text or rich HTML) as blocks, each with an id. */
export const parseBlocks = (value) => {
  // Nothing stored is no blocks — not one empty paragraph of legacy text.
  if (!hasAboutContent(value)) return [];
  const body = parseFragment(aboutToHtml(value));
  const blocks = [];
  let textRun = [];
  let custom = [];

  const flushText = () => {
    if (textRun.length) blocks.push(textRunToBlock(textRun));
    textRun = [];
  };
  const flushCustom = () => {
    if (custom.length) blocks.push({ type: "custom", html: custom.join("") });
    custom = [];
  };
  const push = (block) => {
    flushText();
    flushCustom();
    blocks.push(block);
  };

  for (const node of [...body.childNodes]) {
    if (node.nodeType === 3) {
      if (!node.textContent.trim()) continue;
      flushText();
      custom.push(esc(node.textContent));
      continue;
    }
    if (node.nodeType !== 1) continue;

    const block = hasClass(node, "ep-block")
      ? readBlock(node)
      : hasClass(node, "ep-about-grid")
        ? readLegacyGrid(node)
        : node.getAttribute("data-booking-widget") === "true"
          ? { type: "booking", title: "", text: "" }
          : null;

    if (block) push(block);
    else if (!node.className && BODY_TAGS.has(node.tagName)) {
      flushCustom();
      textRun.push(node);
    } else if (BODY_TAGS.has(node.tagName) && node.tagName === "P" && node.className === "ep-about-lead") {
      flushCustom();
      textRun.push(node);
    } else {
      flushText();
      custom.push(node.outerHTML);
    }
  }
  flushText();
  flushCustom();
  return blocks.map((block) => ({ ...block, id: newId() }));
};

// --- New blocks and starting structures ---------------------------------------

const k = "Dashboard.AboutEditor.defaults";

/** A new block of `type`, filled with copy a business can edit rather than face empty. */
export const createBlock = (type, t) => {
  const text = (key) => t(`${k}.${key}`);
  const blocks = {
    text: { type, title: text("textTitle"), body: `<p>${esc(text("textBody"))}</p>`, align: "center" },
    cards: {
      type,
      title: text("cardsTitle"),
      intro: "",
      cards: [
        { icon: "calendar", title: text("card1Title"), text: text("card1Text") },
        { icon: "people", title: text("card2Title"), text: text("card2Text") },
        { icon: "star", title: text("card3Title"), text: text("card3Text") },
      ],
    },
    media: { type, eyebrow: "", title: text("mediaTitle"), body: `<p>${esc(text("mediaBody"))}</p>`, image: null, side: "left" },
    faq: {
      type,
      title: text("faqTitle"),
      items: [
        { question: text("faq1Question"), answer: text("faq1Answer") },
        { question: text("faq2Question"), answer: text("faq2Answer") },
      ],
    },
    link: { type, text: text("linkText"), href: "", style: "solid", align: "center" },
    booking: { type, title: text("bookingTitle"), text: text("bookingText") },
  };
  return { ...blocks[type], id: newId() };
};

/** "Choose a structure": whole pages to start from, made of the same blocks. */
export const STRUCTURES = {
  complete: ["media", "cards", "faq", "booking"],
  story: ["text", "media", "cards"],
  services: ["cards", "faq", "booking"],
  simple: ["text", "booking"],
};

export const createStructure = (name, t) => STRUCTURES[name].map((type) => createBlock(type, t));

/** How a block is named in the block picker: its type, and its title if it has one. */
export const blockTitle = (block) => {
  const textOf = (html) => (html ? parseFragment(html).textContent : "");
  const title =
    block.title ||
    block.eyebrow ||
    (block.type === "link" ? block.text : "") ||
    textOf(block.body) ||
    textOf(block.html) ||
    "";
  const flat = title.trim().replace(/\s+/g, " ");
  return flat.length > 48 ? `${flat.slice(0, 47)}…` : flat;
};

// --- HTML mode -----------------------------------------------------------------

const VOID = new Set(["BR", "HR", "IMG"]);
const NESTING = new Set(["DIV", "FIGURE", "DETAILS", "UL", "OL"]);

/**
 * Pretty-prints About HTML for the HTML mode: one block-level element per line,
 * containers indented, inline content left exactly as it is.
 */
export const formatAboutHtml = (html) => {
  const lines = [];
  const walk = (node, depth) => {
    const pad = "  ".repeat(depth);
    if (node.nodeType === 3) {
      if (node.textContent.trim()) lines.push(pad + esc(node.textContent.trim()));
      return;
    }
    if (node.nodeType !== 1) return;
    const blockChildren = [...node.childNodes].filter((child) => child.nodeType === 1 || child.textContent.trim());
    const nestable =
      NESTING.has(node.tagName) &&
      blockChildren.length > 0 &&
      blockChildren.every((child) => child.nodeType === 1 && !["A", "STRONG", "EM", "B", "I", "U", "S", "BR"].includes(child.tagName));
    if (!nestable || VOID.has(node.tagName)) {
      lines.push(pad + node.outerHTML);
      return;
    }
    const open = node.outerHTML.slice(0, node.outerHTML.indexOf(">") + 1);
    lines.push(pad + open);
    blockChildren.forEach((child) => walk(child, depth + 1));
    lines.push(`${pad}</${node.tagName.toLowerCase()}>`);
  };
  [...parseFragment(html).childNodes].forEach((node) => walk(node, 0));
  return lines.join("\n");
};

/** How many elements a piece of HTML has: to tell when sanitizing removed some. */
export const countElements = (html) => parseFragment(html).querySelectorAll("*").length;
